import { ReploError } from "schemas/errors";

import { applyJsxNodeUpdate } from "./applyJsxNodeUpdate";
import { injectSourceMetadata, wrapWithUseClient } from "./filesUtils";

// Error class for modifier change operations
class ModifierChangeError extends ReploError {}

interface SelectedElement {
  props: Record<string, any>;
  file: string;
  line: number;
  column: number;
  childText: string;
}

interface ModifierChanges {
  updatedText?: string;
  updatedClassName?: string;
}

interface ApplyModifierChangeParams {
  projectId: string;
  fileId: string;
  selectedElement: SelectedElement;
  changes: ModifierChanges;
  fileContent: string;
  saveFile: (content: string) => Promise<void>;
  updateWebContainerFile: (filePath: string, content: string) => Promise<void>;
  readWebContainerFile?: (filePath: string) => Promise<string>;
}

/**
 * Complete flow for applying modifier changes:
 * 1. Apply JSX modifications
 * 2. Transform file with source metadata
 * 3. Update web container (instant UI update)
 * 4. Save to postgres
 */
export async function applyModifierChange({
  selectedElement,
  changes,
  fileContent,
  saveFile,
  updateWebContainerFile,
}: ApplyModifierChangeParams) {
  try {
    // Step 1: Apply JSX modifications
    const modifiedCode = applyJsxNodeUpdate({
      code: wrapWithUseClient(fileContent),
      updatedText: changes.updatedText,
      updatedClassName: changes.updatedClassName,
      selectedElement,
    });

    // Step 2: Inject source metadata for web container
    const transformedCode = injectSourceMetadata(
      modifiedCode,
      selectedElement.file,
    );

    // Step 3: Update web container (instant UI feedback)
    try {
      await updateWebContainerFile(selectedElement.file, transformedCode);
    } catch (webContainerError) {
      console.warn(
        "Web container update failed (non-critical):",
        webContainerError,
      );
    }

    // Step 4: Save to backend (persistent storage)
    await saveFile(modifiedCode);
  } catch (error) {
    throw new ModifierChangeError({
      message: "Failed to apply modifier change",
      additionalData: {
        originalError: error,
        selectedElementFile: selectedElement.file,
        selectedElementLine: selectedElement.line,
        selectedElementColumn: selectedElement.column,
      },
    });
  }
}
